import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CornerDownRight,
  Eye,
  FileText,
  MessageSquare,
  Plus,
  Search,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { useBoardMenu } from '../../context/BoardMenuContext';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import {
  getBoardCategoryList,
  type BoardCategory,
} from '../../utils/boardCategory';
import {
  isBoardCategoryEnabled,
  parseBoardSettings,
  type BoardListItem,
  type BoardSettings,
} from '../../utils/board';
import {
  fetchQnaBoard,
  getPostListPath,
  getPostMenuId,
  isQnaBoardCode,
} from '../../utils/boardMenu';
import { formatPostListCreatedAt } from '../../utils/dateTime';
import { getPostCreatePath, getPostList, getPostViewPath, type BoardPost } from '../../utils/post';

const STATUS_OPTIONS = [
  { value: 'all', label: '전체 상태' },
  { value: 'ACTIVE', label: '활성' },
  { value: 'INACTIVE', label: '비활성' },
  { value: 'PENDING', label: '대기중' },
];

const PAGE_SIZE = 20;

const PostList: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode: projectCodeFromUrl, boardCode: boardCodeFromUrl } = useParams<{
    projectCode: string;
    boardCode?: string;
  }>();

  const { boards, isLoading: isBoardMenuLoading } = useBoardMenu();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('');
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [categories, setCategories] = useState<BoardCategory[]>([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);

  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [hasNext, setHasNext] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qnaBoard, setQnaBoard] = useState<BoardListItem | null | undefined>(undefined);

  const isQnaRoute = Boolean(boardCodeFromUrl && isQnaBoardCode(boardCodeFromUrl));

  const selectedBoard = useMemo(() => {
    const fromMenu = boards.find((b) => b.code === boardCodeFromUrl);
    if (fromMenu) return fromMenu;
    if (isQnaRoute && qnaBoard) return qnaBoard;
    return undefined;
  }, [boards, boardCodeFromUrl, isQnaRoute, qnaBoard]);
  const boardSettings: BoardSettings = useMemo(
    () => parseBoardSettings(selectedBoard?.settings),
    [selectedBoard?.settings]
  );
  const categoryEnabled = isBoardCategoryEnabled(selectedBoard?.settings);
  const showReaction =
    boardSettings.reaction.includes('LIKE') || boardSettings.reaction.includes('DISLIKE');
  const showLike = boardSettings.reaction.includes('LIKE');
  const showDislike = boardSettings.reaction.includes('DISLIKE');
  const showViewCount = boardSettings.view_count;
  const showCommentCount = boardSettings.comment;
  const replyEnabled = boardSettings.reply;

  const categoryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((c) => map.set(c.code, c.name));
    return map;
  }, [categories]);

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    if (!projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
  }, [projectCodeFromUrl, navigate]);

  useEffect(() => {
    if (!boardCodeFromUrl) return;
    if (isQnaBoardCode(boardCodeFromUrl)) {
      setActiveMenu('qna');
    } else {
      setActiveMenu(getPostMenuId(boardCodeFromUrl));
    }
  }, [boardCodeFromUrl]);

  useEffect(() => {
    if (!projectCodeFromUrl || !isQnaRoute) {
      setQnaBoard(null);
      return;
    }

    let cancelled = false;
    setQnaBoard(undefined);
    fetchQnaBoard(projectCodeFromUrl)
      .then((board) => {
        if (!cancelled) setQnaBoard(board);
      })
      .catch(() => {
        if (!cancelled) setQnaBoard(null);
      });

    return () => {
      cancelled = true;
    };
  }, [projectCodeFromUrl, isQnaRoute]);

  useEffect(() => {
    if (!projectCodeFromUrl || isBoardMenuLoading) return;

    if (isQnaRoute) {
      if (qnaBoard === undefined) return;
      return;
    }

    if (boards.length === 0) return;

    if (!boardCodeFromUrl) {
      navigate(getPostListPath(projectCodeFromUrl, boards[0].code), { replace: true });
      return;
    }

    const exists = boards.some((b) => b.code === boardCodeFromUrl);
    if (!exists) {
      navigate(getPostListPath(projectCodeFromUrl, boards[0].code), { replace: true });
    }
  }, [projectCodeFromUrl, boardCodeFromUrl, boards, isBoardMenuLoading, navigate, isQnaRoute, qnaBoard]);

  useEffect(() => {
    const trimmedSearchTerm = searchTerm.trim();
    if (trimmedSearchTerm.length <= 1) {
      setDebouncedSearchTerm('');
      return;
    }
    const timer = setTimeout(() => setDebouncedSearchTerm(trimmedSearchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedStatus('all');
    setSelectedCategory('all');
    setPosts([]);
    setNextCursor(undefined);
    setHasNext(false);
    setError(null);
  }, [boardCodeFromUrl]);

  useEffect(() => {
    if (!projectCodeFromUrl || !boardCodeFromUrl || !categoryEnabled) {
      setCategories([]);
      return;
    }

    let cancelled = false;
    setIsCategoriesLoading(true);
    getBoardCategoryList(projectCodeFromUrl, boardCodeFromUrl)
      .then((list) => {
        if (!cancelled) setCategories(list);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      })
      .finally(() => {
        if (!cancelled) setIsCategoriesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectCodeFromUrl, boardCodeFromUrl, categoryEnabled]);

  const fetchPosts = useCallback(
    async (cursor?: string, append = false) => {
      if (!projectCodeFromUrl || !boardCodeFromUrl) return;

      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setError(null);
      }

      try {
        const res = await getPostList(projectCodeFromUrl, boardCodeFromUrl, {
          cursor,
          size: PAGE_SIZE,
          keyword: debouncedSearchTerm || undefined,
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          categoryCode: selectedCategory !== 'all' ? selectedCategory : undefined,
        });

        setPosts((prev) => (append ? [...prev, ...res.posts] : res.posts));
        setNextCursor(res.nextCursor);
        setHasNext(res.hasNext);
      } catch (err) {
        const message = err instanceof Error ? err.message : '게시물 목록을 불러오지 못했습니다.';
        if (!append) {
          setError(message);
          setPosts([]);
          setNextCursor(undefined);
          setHasNext(false);
        } else {
          alert(message);
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [
      projectCodeFromUrl,
      boardCodeFromUrl,
      debouncedSearchTerm,
      selectedStatus,
      selectedCategory,
    ]
  );

  useEffect(() => {
    if (!projectCodeFromUrl || !boardCodeFromUrl || !selectedBoard) return;
    fetchPosts();
  }, [
    projectCodeFromUrl,
    boardCodeFromUrl,
    selectedBoard,
    debouncedSearchTerm,
    selectedStatus,
    selectedCategory,
    fetchPosts,
  ]);

  const handleLoadMore = () => {
    if (!hasNext || !nextCursor || isLoadingMore) return;
    fetchPosts(nextCursor, true);
  };

  const handleAddPost = () => {
    if (!projectCodeFromUrl || !boardCodeFromUrl) return;
    navigate(getPostCreatePath(projectCodeFromUrl, boardCodeFromUrl));
  };

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const boardTitle = selectedBoard?.name ?? boardCodeFromUrl ?? '';
  const isPageLoading = isBoardMenuLoading || (isQnaRoute && qnaBoard === undefined);
  const boardSettingsPath = projectCodeFromUrl
    ? `/projects/${projectCodeFromUrl}/boards`
    : '/projects';

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title={isQnaRoute ? '1:1 문의' : '게시판 관리'}
          subtitle={
            boardCodeFromUrl
              ? `${boardTitle} (${boardCodeFromUrl})`
              : projectDetail.name
                ? `${projectDetail.name} (${projectCodeFromUrl})`
                : '게시물을 관리합니다'
          }
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          {isPageLoading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : isQnaRoute && qnaBoard === null ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">1:1 문의 게시판이 없습니다</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                설정 &gt; 게시판 설정에서 게시판 코드가 <strong>QNA</strong>인 게시판을 추가해 주세요.
              </p>
              <button
                type="button"
                onClick={() => navigate(boardSettingsPath)}
                className="mt-6 px-4 py-2 text-sm text-white bg-gray-900 rounded-lg hover:bg-gray-800"
              >
                게시판 설정으로 이동
              </button>
            </div>
          ) : !isQnaRoute && boards.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500">
              등록된 게시판이 없습니다. 설정 &gt; 게시판 설정에서 게시판을 추가해 주세요.
            </div>
          ) : !selectedBoard ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="max-w-full">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                  {error}
                  <button
                    type="button"
                    onClick={() => setError(null)}
                    className="ml-2 text-red-800 underline"
                  >
                    닫기
                  </button>
                </div>
              )}

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                  <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full flex-wrap">
                    <div className="relative flex-1 sm:max-w-xs min-w-[120px]">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="제목으로 검색"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>

                    {categoryEnabled && (
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        disabled={isCategoriesLoading}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-w-[140px]"
                      >
                        <option value="all">전체 카테고리</option>
                        {categories.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="flex gap-2 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={handleAddPost}
                      disabled={!boardCodeFromUrl}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-4 h-4" />
                      게시물 추가
                    </button>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    총 <span className="font-medium text-gray-900">{posts.length}</span>
                    {hasNext ? '+' : ''}개 게시물
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {isLoading ? (
                  <div className="flex justify-center py-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                ) : posts.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <FileText className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">등록된 게시물이 없습니다.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {posts.map((post) => {
                      const categoryName =
                        post.categoryCode != null
                          ? categoryNameMap.get(post.categoryCode) ?? post.categoryCode
                          : null;
                      const depth = post.depth ?? 0;
                      const isReply = replyEnabled && depth > 0;
                      const replyIndentPx = isReply ? Math.min(depth, 4) * 12 : 0;

                      return (
                        <li
                          key={post.code}
                          className={`px-4 sm:px-6 py-4 hover:bg-gray-50 transition-colors ${
                            isReply
                              ? 'bg-slate-50/80 border-l-2 border-blue-300'
                              : 'border-l-2 border-transparent'
                          }`}
                        >
                          <div
                            className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6"
                            style={isReply ? { paddingLeft: replyIndentPx } : undefined}
                          >
                            <div className="flex-1 min-w-0 flex items-start gap-2">
                              {isReply && (
                                <CornerDownRight
                                  className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-500"
                                  aria-hidden
                                />
                              )}
                              <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {categoryEnabled && categoryName && (
                                  <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 shrink-0">
                                    {categoryName}
                                  </span>
                                )}
                                {post.pin != null && post.pin > 0 && (
                                  <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 shrink-0">
                                    고정
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (projectCodeFromUrl && boardCodeFromUrl) {
                                      navigate(
                                        getPostViewPath(
                                          projectCodeFromUrl,
                                          boardCodeFromUrl,
                                          post.code
                                        )
                                      );
                                    }
                                  }}
                                  className={`text-sm font-medium truncate text-left hover:text-blue-600 hover:underline max-w-full ${
                                    isReply ? 'text-gray-800' : 'text-gray-900'
                                  }`}
                                >
                                  {post.title || '(제목 없음)'}
                                </button>
                                {showCommentCount && (
                                  <span className="inline-flex items-center text-sm text-blue-600 shrink-0">
                                    <MessageSquare className="w-3.5 h-3.5 mr-0.5" />
                                    [{post.commentCount}]
                                  </span>
                                )}
                              </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-4 sm:gap-5 text-sm text-gray-500 shrink-0 flex-wrap lg:ml-auto">
                              {showReaction && (
                                <span className="inline-flex items-center gap-3">
                                  {showLike && (
                                    <span className="inline-flex items-center gap-1">
                                      <ThumbsUp className="w-4 h-4 text-gray-400" />
                                      {post.likeCount}
                                    </span>
                                  )}
                                  {showDislike && (
                                    <span className="inline-flex items-center gap-1">
                                      <ThumbsDown className="w-4 h-4 text-gray-400" />
                                      {post.dislikeCount}
                                    </span>
                                  )}
                                </span>
                              )}
                              {showViewCount && (
                                <span className="inline-flex items-center gap-1">
                                  <Eye className="w-4 h-4 text-gray-400" />
                                  {post.viewCount}
                                </span>
                              )}
                              <span className="text-gray-500 tabular-nums">
                                {formatPostListCreatedAt(post.createdAt) || '—'}
                              </span>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {hasNext && !isLoading && (
                  <div className="px-4 py-4 border-t border-gray-200 text-center">
                    <button
                      type="button"
                      onClick={handleLoadMore}
                      disabled={isLoadingMore}
                      className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      {isLoadingMore ? '불러오는 중...' : '더보기'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default PostList;
